import React, {useEffect, useState} from 'react';
import {CatalogHeader} from './components/CatalogHeader';
import {CatalogMarketplaceHeader} from './components/CatalogMarketplaceHeader';
import {CatalogProductsGrid} from './CatalogProductsGrid';
import {FilterBar} from '@/components/FilterBar/FilterBar';
import {
  ITEMS_PAR_PAGE,
  coerceFiltresItemCatalogue as coerce,
  useSearchClient,
} from 'ff-common';
import {Box, Text} from '@chakra-ui/react';
import {RechercheView} from './RechercheView';
import {useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Pagination} from '@/components/Pagination';
import {Chargement} from '@/components/Chargement/Chargement';
import {FiltresItemCatalogue} from 'ff-common/src/hooks/search/use-search';
import {TPath, useTypedQuery} from '@synako/typed-path';
import {useClient} from '@/haligator-factory.tsx';
import {useHalCollection} from '@synako/haligator';

export const CatalogueView: React.FC = () => {
  const filtersHook =
    useTypedQuery<TPath<{Query: FiltresItemCatalogue}>>(coerce);

  const search = useSearchClient();
  const [itemResults, setItemResults] =
    useState<Awaited<ReturnType<typeof search.searchItems>>>();
  const [totalPages, setTotalPages] = useState(1);
  const location = useLocation();
  const term = filtersHook.params.q;
  const {t} = useTranslation('common');

  const marketplaceId = filtersHook.params.marketplace_id;

  const client = useClient();
  const marketplacesNav = client.follow('marketplaces');
  const marketplacesData = useHalCollection(marketplacesNav);
  const marketplaceState = marketplacesData.items?.find(
    (item) => item.data.marketplace_id === marketplaceId,
  );
  const marketplace = marketplaceState?.data;

  useEffect(() => {
    if (search.indexIsBuilt) {
      search.searchItems(filtersHook.params).then((r) => {
        setItemResults(r);
        setTotalPages(Math.ceil(r.count / ITEMS_PAR_PAGE));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersHook.params, search.indexIsBuilt, search.marketplaceReady]);

  const catWeb = filtersHook.params.cat_web?.[0] ?? '';

  if (!search.indexIsBuilt) {
    return <Chargement texte={t('recherche.chargement_catalogue')} />;
  }

  const filtersHookWithChange = {
    ...filtersHook,
    change: (name: keyof typeof filtersHook.params, val: unknown) => {
      filtersHook.changeMultiple({
        [name]: val,
        page: undefined,
      });
    },
  };

  return (
    <Box bg='white'>
      {marketplaceId && marketplace ? (
        <Box pt={{base: 2, md: 4}}>
          <CatalogMarketplaceHeader
            marketplace={marketplace}
            filtersHook={filtersHookWithChange}
            facets={itemResults?.facets}
            marketplaceState={marketplaceState}
          />
        </Box>
      ) : location.pathname.includes('/catalogue') ? (
        <Box pt={{base: 2, md: 4}}>
          <CatalogHeader
            catWeb={catWeb}
            filtersHook={filtersHookWithChange}
            facets={itemResults?.facets}
          />
        </Box>
      ) : (
        <RechercheView
          term={term}
          itemResults={itemResults}
        />
      )}
      {!(marketplaceId && marketplace) && (
        <FilterBar
          itemResults={itemResults}
          filters={filtersHook.params}
          onFilterChange={(key, value) => {
            filtersHook.changeMultiple({
              [key]: value,
              page: undefined,
            });
          }}
          onRemoveAllFilters={(preservePromo) => {
            filtersHook.changeMultiple({
              nature_du_prix: preservePromo ? 'promo' : undefined,
              produit_marche: undefined,
              labels: undefined,
              temperature_livraison: undefined,
              famille_fromage: undefined,
              type_de_lait: undefined,
              traitement_thermique_du_lait: undefined,
              afficher_marketplace: undefined,
              page: undefined,
            });
          }}
          showProduitMarche
          showMarketplace
          showTriCatalogue
          clientNav={client}
          containerProps={{
            px: {base: 0, md: '20px'},
            pt: {base: 0, md: '10px'},
          }}
        />
      )}
      <CatalogProductsGrid
        hitResult={itemResults?.hits ?? []}
        location={location}
      />
      {itemResults?.count === 0 ? (
        <Box
          mt='30px'
          pb='30px'
        >
          <Text
            textAlign='center'
            fontWeight='500'
          >
            {t('catalogue.aucun_produit')}
          </Text>
        </Box>
      ) : (
        <Pagination
          pageActuelle={filtersHook.params.page ?? 1}
          totalPages={totalPages}
          onPageChange={(p) => {
            filtersHook.change('page', p);
          }}
        />
      )}
    </Box>
  );
};
